import React, { useEffect, useState } from "react";
import {
  RouteObject,
  useLocation,
  useNavigate,
  useRoutes,
} from "react-router-dom";

import { HttpStatusCode } from "axios";

import { isArrayEmpty } from "./libs/common";
import { routes } from "./libs/routes";
import { getLocalValue } from "./libs/localStorage";

import { useUserContext } from "./contexts/userContext";
import { useScrollLockContext } from "./contexts/scrollLockContext";

import { getAppVersion, AppVersion } from "./services/etcServices";
import { updateServerVersionInfo } from "./hooks/useNavigateService";

import useLongTermLogin from "./hooks/useLongTermLogin";
import useFullScreenLoading from "./hooks/useFullScreenLoading";
import userMemberInfo from "./hooks/useMemberInfo";
import usePushKey from "./hooks/usePush";
import { useDeviceType } from "./hooks/useDeviceType/Index";
import useAppVersion from "./hooks/useAppVersion";
import { useDisableBackgroundScroll } from "./hooks/useDisableScroll";
import { useMaintenance } from "./hooks/useMaintenance";

import ForceUpdate from "./components/ForceUpdate";

function App() {
  const router = useRoutes(routes as RouteObject[]);
  const location = useLocation();
  const navigate = useNavigate();
  const longTermLogin = useLongTermLogin();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isMaintenanceLoading, setIsMaintenanceLoading] = React.useState(true);
  const deviceType = useDeviceType();
  const [, checkMaintenance] = useMaintenance();
  const [, { updateField }] = useUserContext();
  const [, pushCall] = usePushKey();
  const [appVersionResult, versionCall] = useAppVersion();
  const { loadingUserData } = userMemberInfo({
    updateField,
    pushCall,
    deviceType,
    appVersion: appVersionResult.result,
  });
  const [lock] = useScrollLockContext();
  const [isForceUpdate, setIsForceUpdate] = useState<boolean | undefined>(
    undefined
  );
  const loadingComponent = useFullScreenLoading({
    isLoading: isLoading || isForceUpdate === undefined || isMaintenanceLoading, transparent:true
  });

  useDisableBackgroundScroll({
    isDisabled: !isArrayEmpty(lock.activeElements),
    elementIdList: lock.activeElements,
  });

  const searchParams: { [key: string]: string } = {};
  location.search
    .replace("?", "")
    .split("&")
    .forEach((item) => {
      const [key, value] = item.split("=");
      searchParams[key] = value;
    });

  const afterLoginNavigate = () => {
    if (searchParams?.redirect && !searchParams?.redirect.startsWith("/auth")) {
      navigate(
        `${decodeURIComponent(searchParams?.redirect)}${location.hash}`,
        { replace: true }
      );
    } else {
      navigate("/view", { replace: true });
    }
  };

  const exceptUrl = [
    "/term",
    "/qrTest",
    "/app_install",
    "/callback",
    "/uncert-evacuation",
  ];
  const maintenanceUrl = "/maintenance";

  useEffect(() => {
    if (exceptUrl.some((url) => location.pathname.startsWith(url))) {
      setIsLoading(false);
      setIsForceUpdate(true);
      return;
    }

    if (appVersionResult.result) {
      getAppVersion(deviceType, appVersionResult.result).then((res) => {
        if (res.status === HttpStatusCode.Ok) {
          setIsForceUpdate(res.data.isForceUpdate);
          
          // 전역 변수 업데이트
          updateServerVersionInfo(res.data.osType, res.data.version);
        } else {
          setIsForceUpdate(false);
        }
      });
    } else {
      // 23.12.11 이건우 파트너 요청사항
      // TODO: 샌디앱을 웹으로 이용하는 사업장이 존재하여 우선 임시로 웹 오픈 후 대응 방안이 마련되면 다시 제한 적용 (Layout/Authenticated에도 수정 필요)
      setIsForceUpdate(false);
    }
  }, [appVersionResult]);

  useEffect(() => {
    if (
      searchParams?.redirect ||
      location.pathname.startsWith(maintenanceUrl) ||
      location.pathname === "/"
    ) {
      return;
    }

    checkMaintenance();
  }, [location.pathname]);

  const initializeGA = () => {
    const isProduction = window.location.hostname.includes(
      "lifeapp.sandimall.com"
    );
    const trackingId = isProduction ? "G-S223EFM8R3" : "G-H0EEC947EV";

    if (window.gtag) {
      window.gtag("config", trackingId);
    }
  };

  useEffect(() => {
    versionCall();

    const init = async () => {
      if (
        [...exceptUrl, maintenanceUrl].some((url) =>
          location.pathname.startsWith(url)
        )
      ) {
        setIsLoading(false);
        setIsMaintenanceLoading(false);
        return;
      }

      const isMaintenance = await checkMaintenance().finally(() => {
        setIsMaintenanceLoading(false);
      });

      if (isMaintenance) {
        setIsLoading(false);
        return;
      }

      const result = getLocalValue("jwtToken");
      const autoLoginToken = getLocalValue("AUTO");

      // [부하 테스트 관련] : 같은 로직을 타긴 하지만 논리적으로 달랐던 케이스이므로, 임시적으로 조건문 유지(24.9.23)
      if (!result) {
        if (autoLoginToken) {
          setIsLoading(false);
          const search = location.search?.includes("redirect") ? "" : encodeURIComponent(location.search);
          // navigate(`/auth/login?redirect=${location.pathname}${search}${location.hash}`, { replace: true } );
          longTermLogin("AUTO", () => {
            loadingUserData();
            setIsLoading(false);
            if (searchParams?.redirect) {
              afterLoginNavigate();
            } else {
              console.log("app: redirect 없음");
            }
          });
        } else {
          setIsLoading(false);
          const search = location.search?.includes("redirect") ? "" : encodeURIComponent(location.search);
          navigate( `/auth/login?redirect=${location.pathname}${search}${location.hash}`, { replace: true, });
        }
      } else {
        setIsLoading(false);
      }
      
    };

    init();
    initializeGA();

    const handleStorageChange = () => {
      navigate("/auth/login", { replace: true });
    };

    window.addEventListener("tokenRemove", handleStorageChange);
    return () => {
      window.removeEventListener("tokenRemove", handleStorageChange);
    };
  }, []);

  const render = () => {
    if (isForceUpdate === undefined || isMaintenanceLoading) {
      return <></>;
    } else if (
      isForceUpdate &&
      !exceptUrl.some((url) => location.pathname.startsWith(url))
    ) {
      return <ForceUpdate />;
    } else {
      return router;
    }
  };

  return (
    <>
      {loadingComponent}
      {render()}
    </>
  );
}

export default App;
